/**
 * Resumen Ejecutivo — módulo lateral para la vista de delegado (/delegado).
 *
 * Iter243 · Feedback delegados implementado sobre oportunidades PLACSP en
 * triage (external_opportunities), que es la ÚNICA pantalla accesible para
 * el rol delegado_regional. Las 3 vías para obtener el pliego:
 *   a) Analizar pliego: scraping automático de la ficha pública PLACSP.
 *   b) Siempre visible: lo que ya tenemos sin IA (score, importe, plazo...).
 *   c) Subir PCAP/PPT manualmente si el delegado lo tiene.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import {
  Loader2, Sparkles, Clock, Calendar, Layers, Euro, TrendingUp,
  Target, Trophy, FileSearch, BarChart2, Brain, Upload, ExternalLink,
} from "lucide-react";
import { KpiBox } from "@/components/exec_summary/KpiBox";
import { ComposicionOferta } from "@/components/exec_summary/ComposicionOferta";
import { CurvaEconomica } from "@/components/exec_summary/CurvaEconomica";

const fmtEur = (n) =>
  n == null || Number(n) === 0 ? "—" :
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function ResumenEjecutivoDelegado({ opp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analizando, setAnalizando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/delegado/oportunidades/exec-summary`, { params: { external_id: opp.external_id } });
      setData(r.data);
    } catch { toast.error("No se pudo cargar el resumen ejecutivo"); }
    setLoading(false);
  }, [opp.external_id]);

  useEffect(() => { load(); }, [load]);

  const analizarPliego = async () => {
    setAnalizando(true);
    try {
      await axios.post(`${API}/delegado/oportunidades/analizar-pliego`, { external_id: opp.external_id });
      toast.success("Pliego analizado con IA");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo analizar el pliego automáticamente"); }
    setAnalizando(false);
  };

  const subirPliego = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("external_id", opp.external_id);
      fd.append("file", file);
      await axios.post(`${API}/delegado/oportunidades/upload-pliego`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pliego subido y analizado con IA");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error subiendo/analizando el pliego"); }
    setUploading(false);
  };

  const buscarAdjudicador = async () => {
    setLookingUp(true);
    try {
      await axios.post(`${API}/delegado/oportunidades/lookup-adjudicador`, { external_id: opp.external_id });
      toast.success("Búsqueda online completada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la búsqueda online"); }
    setLookingUp(false);
  };

  if (loading) return <div className="p-6 text-center text-xs text-gray-400" data-testid="delegado-resumen-loading"><Loader2 size={18} className="animate-spin mx-auto mb-2" />Cargando resumen ejecutivo…</div>;

  const kpi = data?.kpi_extraction;
  const info = data?.disponible_sin_ia || {};

  return (
    <div className="space-y-4 text-left" data-testid="delegado-resumen-ejecutivo-content">
      {/* Vía (b): siempre disponible, venga o no de IA */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
        <p className="text-sm font-bold text-slate-800 line-clamp-2">{info.title}</p>
        <p className="text-xs text-slate-500">{info.organism} · {info.municipality}{info.province ? `, ${info.province}` : ""}</p>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <KpiBox label="Importe" value={fmtEur(info.value_eur)} />
          <KpiBox label="Plazo" value={info.deadline ? String(info.deadline).slice(0, 10) : "—"} />
          <KpiBox label="Score IA" value={info.ai_score ?? "—"} />
        </div>
        {info.ai_rationale && <p className="text-[11px] text-slate-500 italic mt-2">{info.ai_rationale}</p>}
        {info.url && (
          <a href={info.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#007934] font-semibold flex items-center gap-1 mt-2">
            <ExternalLink size={10} /> Ver ficha original en PLACSP
          </a>
        )}
      </div>

      {/* KPIs grandes del contrato (vías a/c) */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">KPIs del contrato</h3>
          <div className="flex items-center gap-2">
            <button onClick={analizarPliego} disabled={analizando} data-testid="delegado-analizar-pliego-btn"
              className="text-[11px] text-[#007934] font-semibold flex items-center gap-1 disabled:opacity-50">
              {analizando ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              {kpi ? "Re-analizar pliego" : "Analizar pliego (automático)"}
            </button>
            <label className="text-[11px] text-gray-500 font-semibold flex items-center gap-1 cursor-pointer" data-testid="delegado-subir-pliego-label">
              {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
              Subir PCAP/PPT
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={uploading}
                data-testid="delegado-subir-pliego-input"
                onChange={e => subirPliego(e.target.files?.[0])} />
            </label>
          </div>
        </div>
        {!kpi && !analizando && (
          <p className="text-xs text-gray-400 mb-2">Sin analizar todavía. "Analizar pliego" intenta descargarlo automático de la ficha de PLACSP; si no lo encuentra, puedes subirlo tú.</p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          <KpiBox label="Duración" icon={Clock} value={kpi?.duracion_anios ? `${kpi.duracion_anios} años` : "—"} />
          <KpiBox label="Prórroga" icon={Calendar} value={kpi?.prorroga_meses ? `${kpi.prorroga_meses} meses` : (kpi?.prorroga_texto ? "Sí" : "—")} hint={kpi?.prorroga_texto} />
          <KpiBox label="Lotes" icon={Layers} value={kpi?.lotes_count ?? "—"} hint={(kpi?.lotes_detalle || []).join(" · ")} />
          <KpiBox label="CAPEX pliego" icon={Euro} value={kpi?.capex_pliego_eur ? fmtEur(kpi.capex_pliego_eur) : "—"} hint={kpi?.inversion_detalle} />
          <KpiBox label="OPEX pliego/año" icon={TrendingUp} value={kpi?.opex_pliego_eur_anual ? fmtEur(kpi.opex_pliego_eur_anual) : "—"} />
        </div>
        {kpi?.extracted_at && <p className="text-[10px] text-gray-400 mt-2">Analizado el {String(kpi.extracted_at).slice(0, 16).replace("T", " ")} · fuente: {(kpi.source_docs || []).join(", ") || "—"} · confianza {kpi.confianza || "—"}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Mapa + mercado cercano */}
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Target size={12} />Ubicación y mercado cercano</h3>
          {data?.geo ? (
            <iframe title="mapa-municipio" data-testid="delegado-resumen-mapa" className="w-full h-36 rounded-lg border border-gray-200"
              src={`https://maps.google.com/maps?q=${data.geo.lat},${data.geo.lng}&z=10&output=embed`} loading="lazy" />
          ) : <div className="h-36 rounded-lg bg-gray-50 flex items-center justify-center text-xs text-gray-400">Sin ubicación geocodificada</div>}
          {data?.nearby_market && (
            <p className="text-[11px] text-gray-600 mt-2" data-testid="delegado-resumen-nearby-market">
              Mercado en <b>{data.nearby_market.provincia}</b>: <b>{data.nearby_market.n_licitaciones}</b> licitaciones por {fmtEur(data.nearby_market.importe_total)} en total.
            </p>
          )}
        </div>

        {/* Adjudicador actual */}
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Trophy size={12} />Adjudicador / gestor actual</h3>
          {data?.adjudicador_actual ? (
            <div data-testid="delegado-resumen-adjudicador">
              <p className="text-sm font-semibold text-gray-800">{data.adjudicador_actual}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Fuente: {data.fuente_adjudicador}</p>
            </div>
          ) : <p className="text-xs text-gray-400 mb-2">Sin dato interno ni mención en el pliego.</p>}
          <button onClick={buscarAdjudicador} disabled={lookingUp} data-testid="delegado-lookup-adjudicador-btn"
            className="text-xs text-[#007934] font-semibold flex items-center gap-1 mt-2 disabled:opacity-50">
            {lookingUp ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />}
            Buscar online (verificar)
          </button>
        </div>
      </div>

      {/* Composición de la oferta */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><BarChart2 size={12} />Composición de la oferta</h3>
        <ComposicionOferta kpi={kpi} />
      </div>

      {/* Curva económica */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><TrendingUp size={12} />Curva económica — simulador de escenarios</h3>
        <CurvaEconomica
          kpi={kpi}
          curve={data?.economic_curve}
          simulateFn={async (v) => (await axios.post(`${API}/delegado/oportunidades/simulate-economica`, { external_id: opp.external_id, baja_pct: v })).data}
        />
      </div>

      {/* Inteligencia de mercado / CRM */}
      {data?.client_intel && (
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="delegado-resumen-client-intel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5"><Brain size={12} />Inteligencia de mercado — {data.client_intel.cliente}</h3>
            <a href="/tender-intelligence" target="_blank" rel="noreferrer" className="text-[10px] text-[#007934] font-semibold flex items-center gap-0.5">Ver completo <ExternalLink size={10} /></a>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <KpiBox label="Ofertas" value={data.client_intel.summary?.ofertas_count ?? 0} />
            <KpiBox label="Ganadas VSM" value={data.client_intel.summary?.ganadas_vsm ?? 0} />
            <KpiBox label="Win rate" value={data.client_intel.summary?.win_rate_pct != null ? `${data.client_intel.summary.win_rate_pct}%` : "n/d"} />
          </div>
          {data.client_intel.competitors_stats?.length > 0 && (
            <div className="text-[11px] text-gray-600 mt-2"><b>Competidores:</b> {data.client_intel.competitors_stats.map(c => c.name).join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
}
